import type { StyleSpecification } from 'maplibre-gl';

import { createCodexMapStyle, type Coordinate } from './codex-map';

const PUBLIC_OSM_TILE_HOSTS = new Set([
  'tile.openstreetmap.org',
  'a.tile.openstreetmap.org',
  'b.tile.openstreetmap.org',
  'c.tile.openstreetmap.org',
]);

function isPublicOsmTileUrl(value: string): boolean {
  try {
    return PUBLIC_OSM_TILE_HOSTS.has(new URL(value).hostname.toLowerCase());
  } catch {
    return false;
  }
}

export interface ExploreMapProvider {
  mode: 'managed-vector' | 'local-illustrated';
  style: string | StyleSpecification;
  attribution: readonly { href: string; label: string }[];
  notice: string;
}

interface MapProviderEnvironment {
  VITE_MAP_STYLE_URL?: string;
  VITE_MAP_PROVIDER_NAME?: string;
  VITE_MAP_PROVIDER_URL?: string;
}

/** The same-origin proxy deliberately emits relative URLs. MapLibre requires absolute
 * sprite URLs; resolve resources against the original style location, not a new host.
 * This changes neither the provider nor credentials/CSP/egress permissions.
 */
export function resolveMapStyleResources(
  style: StyleSpecification,
  styleLocation: string,
  pageLocation: string,
): StyleSpecification {
  const base = new URL(styleLocation, pageLocation);
  // URL serialization encodes braces, but MapLibre must still substitute its
  // font/tile placeholders before issuing the request.
  const absolute = (url: string) =>
    new URL(url, base).href.replace(/%7B/gi, '{').replace(/%7D/gi, '}');
  return {
    ...style,
    ...(style.sprite
      ? {
          sprite:
            typeof style.sprite === 'string'
              ? absolute(style.sprite)
              : style.sprite.map((sprite) => ({ ...sprite, url: absolute(sprite.url) })),
        }
      : {}),
    ...(style.glyphs ? { glyphs: absolute(style.glyphs) } : {}),
    sources: Object.fromEntries(
      Object.entries(style.sources).map(([id, source]) => [
        id,
        {
          ...source,
          ...('url' in source && typeof source.url === 'string'
            ? { url: absolute(source.url) }
            : {}),
          ...('tiles' in source && source.tiles ? { tiles: source.tiles.map(absolute) } : {}),
          ...('data' in source && typeof source.data === 'string'
            ? { data: absolute(source.data) }
            : {}),
        },
      ]),
    ),
  };
}

function safeHttpsUrl(value: string): URL | undefined {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
}

export function isAllowedMapStyleUrl(value: string): boolean {
  const candidate = value.trim();
  if (candidate.startsWith('/') && !candidate.startsWith('//')) return true;
  const url = safeHttpsUrl(candidate);
  return Boolean(url && !isPublicOsmTileUrl(url.toString()));
}

export function styleUsesDirectPublicOsmTiles(style: Pick<StyleSpecification, 'sources'>): boolean {
  return Object.values(style.sources).some((source) => {
    const candidate = source as { tiles?: string[]; url?: string };
    return [candidate.url, ...(candidate.tiles ?? [])].some(
      (url) => typeof url === 'string' && isPublicOsmTileUrl(url),
    );
  });
}

export function resolveExploreMapProvider(
  environment: MapProviderEnvironment,
  center: Coordinate,
): ExploreMapProvider {
  const styleUrl = environment.VITE_MAP_STYLE_URL?.trim();
  if (!styleUrl || !isAllowedMapStyleUrl(styleUrl)) {
    return {
      mode: 'local-illustrated',
      style: createCodexMapStyle(center),
      attribution: [],
      notice: 'Local illustrated map · no live basemap data',
    };
  }

  const providerName = environment.VITE_MAP_PROVIDER_NAME?.trim();
  const providerUrl = environment.VITE_MAP_PROVIDER_URL?.trim();
  const safeProviderUrl = providerUrl ? safeHttpsUrl(providerUrl) : undefined;
  return {
    mode: 'managed-vector',
    style: styleUrl,
    attribution: [
      {
        href: 'https://www.openstreetmap.org/copyright',
        label: '© OpenStreetMap contributors',
      },
      ...(providerName && safeProviderUrl
        ? [{ href: safeProviderUrl.toString(), label: providerName }]
        : []),
    ],
    notice: providerName ? `Live streets · ${providerName}` : 'Live streets · managed map',
  };
}
